import { useCallback, useEffect, useState } from 'react';

import {
  countAdminSubmissionsNew,
  listAdminSubmissions,
  type AdminSubmissionRowJoined,
  type AdminSubmissionsStatusFilter,
} from '@/lib/db/admin/submissions';

export type AdminSubmissionsFilter = {
  status: AdminSubmissionsStatusFilter;
  /** Server-side ilike over reference / seller / location columns (ADR-056). */
  searchQuery: string;
  /** 0-based page index (ADR-056 — server pagination, §4.10). */
  page: number;
  pageSize: number;
};

export type AdminSubmissionsState = {
  status: 'loading' | 'success' | 'error';
  rows: AdminSubmissionRowJoined[];
  /** Exact count for the ACTIVE filters (drives the pagination bar). */
  total: number;
  /** GLOBAL `new` count — independent of page and filters (ADR-056). */
  newCount: number;
  error: Error | null;
  refetch: () => void;
};

const INITIAL: Omit<AdminSubmissionsState, 'refetch'> = {
  status: 'loading',
  rows: [],
  total: 0,
  newCount: 0,
  error: null,
};

export function useAdminSubmissions(filter: AdminSubmissionsFilter): AdminSubmissionsState {
  const [state, setState] = useState<Omit<AdminSubmissionsState, 'refetch'>>(INITIAL);
  const [tick, setTick] = useState(0);

  const { status, searchQuery, page, pageSize } = filter;

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));

    // allSettled, NOT all: the badge counter is an independent triage
    // signal — a transient failure of the lightweight HEAD count must not
    // blank an already-fetched page of rows (cf. Promise.all masking 400s
    // on the KPI tiles). Only a LIST failure flips the table to error;
    // a lone count failure keeps the previous badge value.
    void Promise.allSettled([
      listAdminSubmissions({ status, searchQuery, pageSize, offset: page * pageSize }),
      countAdminSubmissionsNew(),
    ]).then(([listResult, countResult]) => {
      if (cancelled) return;
      if (listResult.status === 'rejected') {
        const err = listResult.reason as unknown;
        setState({
          status: 'error',
          rows: [],
          total: 0,
          newCount: 0,
          error: err instanceof Error ? err : new Error(String(err)),
        });
        return;
      }
      setState((prev) => ({
        status: 'success',
        rows: listResult.value.rows,
        total: listResult.value.total,
        newCount: countResult.status === 'fulfilled' ? countResult.value : prev.newCount,
        error: null,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [status, searchQuery, page, pageSize, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  return { ...state, refetch };
}
